<template>
  <div class="mail-history-page">
    <!-- Header -->
    <b-card no-body>
      <b-card-body class="py-2">
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center">
            <div class="mh-icon mr-1">
              <i class="fas fa-envelope"></i>
            </div>
            <div>
              <div class="mh-title">{{ msg('Mail History') }}</div>
              <div class="mh-subtitle">{{ msg('Browse all sent emails') }}</div>
            </div>
          </div>
          <div class="d-flex align-items-center mt-1 mt-md-0">
            <b-button variant="primary" class="mh-primary-btn" @click="refresh()">
              <i class="fas fa-sync-alt mr-50"></i> {{ msg('Reload') }}
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Filters -->
    <b-card no-body>
      <b-card-body class="py-2">
        <b-row>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <label class="mh-label">{{ msg('Search') }}</label>
            <b-input-group class="mh-search-group">
              <b-form-input
                  v-model="filter.like.search"
                  :placeholder="msg('Recipient, subject...')"
                  @keyup.enter="refresh()"
              />
              <b-input-group-append>
                <b-button variant="outline-primary" @click="refresh()">
                  <i class="fas fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <label class="mh-label">{{ msg('From') }}</label>
            <b-form-datepicker
                v-model="filter.gte.sentAt"
                :placeholder="msg('Start date')"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                reset-button
                @input="refresh()"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <label class="mh-label">{{ msg('To') }}</label>
            <b-form-datepicker
                v-model="filter.lte.sentAt"
                :placeholder="msg('End date')"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                reset-button
                @input="refresh()"
            />
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button variant="outline-secondary" block @click="clearFilters()">
              <i class="fas fa-times mr-50"></i> {{ msg('Clear') }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table -->
    <b-card no-body class="mh-content-card">
      <b-card-body>
        <b-table
            ref="mail-history-table"
            striped
            responsive
            hover
            :items="mails"
            :fields="fields"
            :busy="loading"
            @sort-changed="onSort"
            show-empty
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mx-1"></b-spinner>
              <strong>{{ msg('Loading') }}...</strong>
            </div>
          </template>

          <template #empty>
            <div class="text-center my-4">
              <div style="font-size:34px; opacity:.5"><i class="far fa-folder-open"></i></div>
              <div class="font-weight-bold">{{ msg('No emails found') }}</div>
              <div class="text-muted" style="font-size:12px">{{ msg('Try different filters or date range') }}</div>
            </div>
          </template>

          <template #cell(formId)="data">
            <b-badge variant="light-secondary" class="mh-badge">
              {{ data.value || '-' }}
            </b-badge>
          </template>

          <template #cell(clientName)="data">
            <div class="d-flex align-items-center">
              <i class="fas fa-user-circle mr-50 text-muted"></i>
              <span class="font-weight-bold">{{ data.value || '-' }}</span>
            </div>
          </template>

          <template #cell(mailType)="data">
            <b-badge variant="light-primary" class="mh-badge">
              {{ data.value || '-' }}
            </b-badge>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="getStatusVariant(data.value)" class="mh-status-badge">
              <i :class="getStatusIcon(data.value)" class="mr-25"></i>
              {{ data.value || '-' }}
            </b-badge>
          </template>

          <template #cell(sentAt)="data">
            <span v-if="data.value">{{ formatDate(data.value) }}</span>
            <span v-else class="text-muted">-</span>
          </template>

          <template #cell(userId)="data">
             <span class="font-weight-bold">{{ getUserFullName(data.value) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-button
                size="sm"
                variant="flat-primary"
                class="btn-icon"
                @click="openDetail(data.item)"
                v-b-tooltip.hover
                :title="msg('Details')"
            >
              <i class="fas fa-eye"></i>
            </b-button>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex align-items-center justify-content-between mt-2 flex-wrap">
          <div class="mh-total text-muted">
            <strong>{{ msg('Total') }}:</strong> {{ page.total }}
          </div>
          <b-pagination
              v-model="page.currentPage"
              :total-rows="page.total"
              :per-page="page.amount"
              first-number
              last-number
              @input="refresh"
              class="mh-pagination mb-0"
          />
        </div>
      </b-card-body>
    </b-card>

    <!-- Detail Modal -->
    <b-modal
        v-model="dialogs.detail"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        body-class="mh-modal-body"
        content-class="mh-modal-content"
    >
      <template #modal-title>
        <div class="d-flex align-items-center">
          <div class="mh-icon mr-1"><i class="fas fa-envelope-open-text"></i></div>
          <div>
            <div class="mh-title" style="font-size:15px">{{ msg('Email Details') }}</div>
            <div class="mh-subtitle">{{ selectedMail && selectedMail.mailType || '' }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedMail" class="mh-detail-wrap">
        <div class="mh-detail-grid">
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Form ID') }}</div>
            <div class="mh-detail-value">{{ selectedMail.formId || '-' }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Typeform ID') }}</div>
            <div class="mh-detail-value">{{ selectedMail.typeFormId || '-' }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Client') }}</div>
            <div class="mh-detail-value">{{ selectedMail.clientName || '-' }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Branch') }}</div>
            <div class="mh-detail-value">{{ selectedMail.buid || '-' }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Mail Type') }}</div>
            <div class="mh-detail-value">{{ selectedMail.mailType || '-' }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Status') }}</div>
            <div class="mh-detail-value">
              <b-badge :variant="getStatusVariant(selectedMail.status)">
                {{ selectedMail.status || '-' }}
              </b-badge>
            </div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('Sent At') }}</div>
            <div class="mh-detail-value">{{ formatDate(selectedMail.sentAt) }}</div>
          </div>
          <div class="mh-detail-row">
            <div class="mh-detail-label">{{ msg('User') }}</div>
            <div class="mh-detail-value">{{ selectedMail.userId || '-' }}</div>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="outline-secondary" @click="dialogs.detail = false">
            {{ msg('Close') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'MailHistory',
  data() {
    return {
      loading: true,
      mails: [],
      filter: {
        like: {search: ''},
        eq: {},
        lte: {sentAt: null},
        gte: {sentAt: null},
        in: {}
      },
      sort: {by: 'sentAt', desc: true, direction: 'desc'},
      page: {amount: 20, currentPage: 1, total: 0},
      dialogs: {detail: false},
      selectedMail: null,
      listeners: {refresh: () => {}},
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getUserFullName', 'getUserAcronyms']),

    fields() {
      return [
        {key: 'formId', label: this.msg('Form'), sortable: true},
        {key: 'clientName', label: this.msg('Client'), sortable: true},
        {key: 'mailType', label: this.msg('Mail Type'), sortable: true},
        {key: 'status', label: this.msg('Status'), sortable: true},
        {key: 'sentAt', label: this.msg('Sent At'), sortable: true},
        {key: 'userId', label: this.msg('User'), sortable: true},
        {key: 'actions', label: this.msg('Actions'), sortable: false, class: 'text-center'},
      ]
    }
  },
  created() {
    const $this = this
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => $this.refresh()))
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh)
  },
  methods: {
    ...mapActions('typeform', ['getMailHistory']),

    refresh() {
      this.loading = true
      this.getMailHistory({
        page: this.page.currentPage,
        amount: this.page.amount,
        filter: this.filter,
        sortcolumn: this.sort.by,
        sortby: this.sort.direction,
      }).then(this.onRetrieve)
          .catch(() => {
            this.loading = false
          })
    },

    onRetrieve(data) {
      this.page = {
        amount: data['Amount'] || this.page.amount,
        total: data['Total'] || 0,
        currentPage: data['Page'] || this.page.currentPage,
      }
      this.mails = data['TypeformMails'] || data['Mails'] || []
      this.loading = false
    },

    onSort(ctx) {
      this.sort.by = ctx.sortBy
      this.sort.desc = ctx.sortDesc
      this.sort.direction = ctx.sortDesc ? 'desc' : 'asc'
      this.refresh()
    },

    clearFilters() {
      this.filter = {
        like: {search: ''},
        eq: {},
        lte: {sentAt: null},
        gte: {sentAt: null},
        in: {}
      }
      this.page.currentPage = 1
      this.refresh()
    },

    openDetail(item) {
      this.selectedMail = item
      this.dialogs.detail = true
    },

    getStatusVariant(status) {
      if (!status) return 'light-success'
      const s = String(status).toUpperCase()
      if (s === 'SENT' || s === 'SUCCESS' || s === 'DELIVERED') return 'light-success'
      if (s === 'FAILED' || s === 'ERROR') return 'light-danger'
      if (s === 'PENDING' || s === 'QUEUED') return 'light-warning'
      return 'light-info'
    },

    getStatusIcon(status) {
      if (!status) return 'fas fa-check'
      const s = String(status).toUpperCase()
      if (s === 'SENT' || s === 'SUCCESS' || s === 'DELIVERED') return 'fas fa-check'
      if (s === 'FAILED' || s === 'ERROR') return 'fas fa-times'
      if (s === 'PENDING' || s === 'QUEUED') return 'fas fa-clock'
      return 'fas fa-info-circle'
    },

    formatDate(val) {
      if (!val) return '-'
      if (this.$moment) return this.$moment(val).format('DD.MM.YYYY HH:mm')
      return String(val)
    },
  }
}
</script>

<style scoped>
.mail-history-page {
  padding-bottom: 20px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(115, 103, 240, .06), transparent 60%),
  radial-gradient(1000px 380px at 90% 10%, rgba(0, 184, 148, .06), transparent 55%);
  border-radius: 18px;
}

.mh-content-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .06);
  box-shadow: 0 10px 26px rgba(0, 0, 0, .06);
  backdrop-filter: blur(6px);
}

.mh-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 103, 240, .14);
  color: #7367f0;
}

.mh-title {
  font-weight: 900;
  font-size: 16px;
  line-height: 1.1;
  letter-spacing: .2px;
}

.mh-subtitle {
  font-size: 12px;
  opacity: .78;
}

.mh-primary-btn {
  border-radius: 12px;
}

.mh-label {
  font-size: 11px;
  opacity: .75;
  font-weight: 600;
}

.mh-search-group .form-control {
  border-radius: 12px 0 0 12px;
}

.mh-search-group .btn {
  border-radius: 0 12px 12px 0;
}

.mh-badge {
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 10px;
}

.mh-status-badge {
  font-weight: 700;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11px;
}

.mh-pagination :deep(.page-link) {
  border-radius: 10px !important;
}

.mh-total {
  font-size: 12px;
}

/* Modal */
.mh-modal-content {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .10);
  box-shadow: 0 18px 50px rgba(0, 0, 0, .18);
}

.mh-modal-body {
  padding: 18px;
}

.mh-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

@media (max-width: 768px) {
  .mh-detail-grid {
    grid-template-columns: 1fr;
  }
}

.mh-detail-row {
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 14px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, .012);
  position: relative;
  overflow: hidden;
}

.mh-detail-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(180deg, rgba(115, 103, 240, .9), rgba(115, 103, 240, .25));
}

.mh-detail-label {
  font-size: 11px;
  opacity: .7;
  font-weight: 700;
  margin-bottom: 2px;
  padding-left: 6px;
}

.mh-detail-value {
  font-size: 13px;
  font-weight: 800;
  word-break: break-word;
  padding-left: 6px;
}

.mh-body-preview {
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 14px;
  padding: 14px;
  background: rgba(0, 0, 0, .012);
  max-height: 300px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
}
</style>
